/**
 * One requestAnimationFrame loop for the whole application.
 *
 * Every live element -- gauges, charts, sparklines, and the numeric readouts in
 * the signals table -- subscribes here and paints itself straight from the
 * signal store. React never sees a value change, so incoming data at 20 Hz
 * costs zero reconciliation regardless of how many widgets are on screen.
 *
 * Each subscriber declares its own minimum interval, so a 30-second signal is
 * not repainted sixty times a second just because a tachometer is.
 */
import { useEffect, useLayoutEffect, useRef, type MutableRefObject } from 'react';

interface Subscriber {
  fn: (now: number) => void;
  minInterval: number;
  last: number;
}

const subscribers = new Set<Subscriber>();
let rafHandle = 0;

function tick(now: number): void {
  for (const sub of subscribers) {
    if (now - sub.last < sub.minInterval) continue;
    sub.last = now;
    try {
      sub.fn(now);
    } catch (err) {
      // One broken widget must not take down every other widget's painting.
      console.error('render subscriber failed', err);
    }
  }
  rafHandle = subscribers.size > 0 ? requestAnimationFrame(tick) : 0;
}

export function subscribeFrame(fn: (now: number) => void, minIntervalMs = 0): () => void {
  const sub: Subscriber = { fn, minInterval: minIntervalMs, last: 0 };
  subscribers.add(sub);
  if (rafHandle === 0) rafHandle = requestAnimationFrame(tick);
  return () => {
    subscribers.delete(sub);
  };
}

export type PaintFn = (ctx: CanvasRenderingContext2D, width: number, height: number, now: number) => void;

/**
 * Attach a canvas that repaints on the shared loop.
 *
 * Handles device-pixel-ratio scaling and element resizes, so `draw` always
 * works in CSS pixels and never has to think about retina displays.
 */
export function useCanvasPainter(draw: PaintFn, minIntervalMs = 0): MutableRefObject<HTMLCanvasElement | null> {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawRef = useRef(draw);
  const sizeRef = useRef({ w: 0, h: 0 });

  // Keep the latest closure without resubscribing every render.
  useLayoutEffect(() => {
    drawRef.current = draw;
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // The signals table can hold a couple of hundred sparklines. Painting the
    // ones scrolled out of view is pure waste, so they idle until visible.
    let visible = true;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) visible = entry.isIntersecting;
      },
      { rootMargin: '120px' },
    );
    io.observe(canvas);

    const resize = (): void => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
      const w = Math.max(1, Math.round(rect.width));
      const h = Math.max(1, Math.round(rect.height));
      if (sizeRef.current.w === w && sizeRef.current.h === h) return;
      sizeRef.current = { w, h };
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const unsubscribe = subscribeFrame((now) => {
      if (!visible) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const { w, h } = sizeRef.current;
      if (w === 0 || h === 0) return;
      const dpr = canvas.width / w;
      ctx.save();
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      drawRef.current(ctx, w, h, now);
      ctx.restore();
    }, minIntervalMs);

    return () => {
      unsubscribe();
      ro.disconnect();
      io.disconnect();
    };
  }, [minIntervalMs]);

  return canvasRef;
}

/**
 * Drive an element's text content from the render loop.
 *
 * Used for the live numbers in the signals table: writing textContent on a few
 * hundred rows is cheap, whereas re-rendering them through React at 10 Hz is
 * not.
 */
export function useLiveText<T extends HTMLElement>(compute: () => string, minIntervalMs = 100): MutableRefObject<T | null> {
  const ref = useRef<T | null>(null);
  const computeRef = useRef(compute);

  useLayoutEffect(() => {
    computeRef.current = compute;
  });

  useEffect(() => {
    let previous = '';
    return subscribeFrame(() => {
      const el = ref.current;
      if (!el) return;
      const next = computeRef.current();
      // Skip the DOM write when nothing changed; most signals hold steady.
      if (next !== previous) {
        previous = next;
        el.textContent = next;
      }
    }, minIntervalMs);
  }, [minIntervalMs]);

  return ref;
}

/** Read a CSS custom property off the document root, with a fallback. */
export function cssVar(name: string, fallback = '#888'): string {
  if (typeof document === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

/**
 * Theme tokens cached for the paint loop.
 *
 * getComputedStyle is far too slow to call per frame per widget, so the values
 * are read once and refreshed only when the theme actually changes.
 */
class ThemeCache {
  private cache = new Map<string, string>();
  private generation = 0;

  get(name: string, fallback = '#888'): string {
    const hit = this.cache.get(name);
    if (hit !== undefined) return hit;
    const value = cssVar(name, fallback);
    this.cache.set(name, value);
    return value;
  }

  invalidate(): void {
    this.cache.clear();
    this.generation++;
  }

  get version(): number {
    return this.generation;
  }
}

export const theme = new ThemeCache();

export const SERIES_VARS = [
  '--series-1',
  '--series-2',
  '--series-3',
  '--series-4',
  '--series-5',
  '--series-6',
  '--series-7',
  '--series-8',
] as const;

/**
 * Colour for series index `i`.
 *
 * Slots are assigned in fixed order and never cycled: past eight series the UI
 * refuses to add more rather than reusing a hue that a reader has already
 * learned to mean something else.
 */
export function seriesColor(i: number): string {
  const varName = SERIES_VARS[i] ?? SERIES_VARS[SERIES_VARS.length - 1];
  return theme.get(varName as string, '#3987e5');
}

export const MAX_SERIES = SERIES_VARS.length;
